import {
  closeWebSocketWhenReady,
  type AuthenticatedWebSocketConfig,
} from "@/lib/websocket";

export type RealtimeConnectionState =
  "connected" | "connecting" | "disconnected";

interface Listener {
  onMessage: (event: MessageEvent<string>) => void;
  onState: (state: RealtimeConnectionState) => void;
}

interface Connection {
  listeners: Set<Listener>;
  socket: WebSocket | null;
  timer: ReturnType<typeof setTimeout> | null;
  retries: number;
  state: RealtimeConnectionState;
  /** Project channels are closed while the tab stays hidden. */
  suspendWhenHidden: boolean;
  suspended: boolean;
  connect: () => void;
  reconnect: () => void;
  close: () => void;
  suspend: () => void;
  resume: () => void;
}

// Only active subscribers retain a connection. Authentication subprotocols are
// part of the identity: different accounts/origins never share a transport.
const connections = new Map<string, Connection>();

/**
 * A tab hidden this long closes its project channels (`/ws/project/<id>/`:
 * presence, comments and ticket list events). They reopen when the tab is
 * visible again; ticket lists catch up through `/api/tickets/changes/` on
 * reconnect. Ticket lock channels (`/ws/tickets/<id>/semaphore/`) and the
 * notifications channel are never suspended, so an open ticket keeps its
 * lock heartbeat while the tab is in the background.
 */
export const HIDDEN_TAB_SOCKET_SUSPEND_MS = 5 * 60 * 1000;

const PROJECT_CHANNEL_PATH = /^\/ws\/project\/[^/]+\/?$/;

function suspendsWhenHidden(url: string): boolean {
  try {
    return PROJECT_CHANNEL_PATH.test(new URL(url).pathname);
  } catch {
    return false;
  }
}

let hiddenSuspendTimer: ReturnType<typeof setTimeout> | null = null;
let hiddenSuspendActive = false;
let visibilityListenerInstalled = false;

function isDocumentHidden(): boolean {
  return (
    typeof document !== "undefined" && document.visibilityState === "hidden"
  );
}

function suspendHiddenConnections() {
  hiddenSuspendTimer = null;
  if (!isDocumentHidden()) return;
  hiddenSuspendActive = true;
  for (const connection of connections.values()) {
    if (connection.suspendWhenHidden) connection.suspend();
  }
}

function handleVisibilityChange() {
  if (isDocumentHidden()) {
    if (hiddenSuspendTimer === null) {
      hiddenSuspendTimer = setTimeout(
        suspendHiddenConnections,
        HIDDEN_TAB_SOCKET_SUSPEND_MS
      );
    }
    return;
  }
  if (hiddenSuspendTimer !== null) clearTimeout(hiddenSuspendTimer);
  hiddenSuspendTimer = null;
  if (!hiddenSuspendActive) return;
  hiddenSuspendActive = false;
  for (const connection of connections.values()) connection.resume();
}

function installVisibilityListener() {
  if (visibilityListenerInstalled || typeof document === "undefined") return;
  visibilityListenerInstalled = true;
  document.addEventListener("visibilitychange", handleVisibilityChange);
  // The first subscriber may mount in a tab that is already hidden.
  if (isDocumentHidden()) handleVisibilityChange();
}

export function subscribeRealtimeSocket(
  config: AuthenticatedWebSocketConfig,
  listener: Listener
): {
  release: () => void;
  reconnect: () => void;
  send: (data: string) => boolean;
} {
  const key = JSON.stringify([config.url, config.protocols]);
  let connection = connections.get(key);
  if (!connection) {
    const suspendWhenHidden = suspendsWhenHidden(config.url);
    if (suspendWhenHidden) installVisibilityListener();
    const current: Connection = {
      listeners: new Set(),
      socket: null,
      timer: null,
      retries: 0,
      state: "disconnected",
      suspendWhenHidden,
      suspended: suspendWhenHidden && hiddenSuspendActive,
      connect: () => {},
      reconnect: () => {},
      close: () => {},
      suspend: () => {},
      resume: () => {},
    };
    const publishState = (state: RealtimeConnectionState) => {
      current.state = state;
      for (const subscriber of current.listeners) subscriber.onState(state);
    };
    current.close = () => {
      if (current.timer !== null) clearTimeout(current.timer);
      current.timer = null;
      const socket = current.socket;
      current.socket = null;
      if (socket) {
        socket.onopen = null;
        socket.onclose = null;
        socket.onmessage = null;
        socket.onerror = null;
        closeWebSocketWhenReady(socket);
      }
    };
    const retry = () => {
      if (!current.listeners.size || current.retries >= 10) {
        publishState("disconnected");
        return;
      }
      current.retries += 1;
      publishState("connecting");
      current.timer = setTimeout(
        () => {
          current.timer = null;
          current.connect();
        },
        Math.min(1000 * 2 ** current.retries, 30_000)
      );
    };
    current.connect = () => {
      if (!current.listeners.size || current.socket || current.suspended)
        return;
      publishState("connecting");
      try {
        const socket = new WebSocket(config.url, config.protocols);
        current.socket = socket;
        socket.onopen = () => {
          if (current.socket !== socket) return;
          current.retries = 0;
          publishState("connected");
        };
        socket.onmessage = (event: MessageEvent<string>) => {
          if (current.socket !== socket) return;
          for (const subscriber of current.listeners) {
            try {
              subscriber.onMessage(event);
            } catch (error) {
              // One consumer must not prevent other views receiving the event.
              console.error("Realtime event handler failed", error);
            }
          }
        };
        socket.onclose = () => {
          if (current.socket !== socket) return;
          current.socket = null;
          retry();
        };
        socket.onerror = () => {
          if (current.socket === socket) closeWebSocketWhenReady(socket);
        };
      } catch {
        retry();
      }
    };
    current.reconnect = () => {
      current.close();
      current.retries = 0;
      current.connect();
    };
    current.suspend = () => {
      if (current.suspended) return;
      current.suspended = true;
      current.close();
      current.retries = 0;
      publishState("disconnected");
    };
    current.resume = () => {
      if (!current.suspended) return;
      current.suspended = false;
      current.retries = 0;
      current.connect();
    };
    connection = current;
    connections.set(key, connection);
  }

  const current = connection;
  current.listeners.add(listener);
  listener.onState(current.state);
  if (!current.socket && current.timer === null) current.connect();
  let released = false;
  return {
    send: (data) => {
      if (released || current.socket?.readyState !== WebSocket.OPEN)
        return false;
      current.socket.send(data);
      return true;
    },
    reconnect: () => {
      if (!released) current.reconnect();
    },
    release: () => {
      if (released) return;
      released = true;
      current.listeners.delete(listener);
      // StrictMode and same-commit subscriber handoffs reuse the connection.
      // No timer or socket survives after the last subscriber stays unmounted.
      queueMicrotask(() => {
        if (current.listeners.size || connections.get(key) !== current) return;
        connections.delete(key);
        current.close();
      });
    },
  };
}
